import {
    ActionButton,
    BusinessPicker,
    OfficeHeader,
    Paginator,
    RegularInsurerPicker,
    RegularSelectInput,
    Show,
    TableComponent,
} from "components";
import { FC, useMemo } from "react";
import {
    Payment_status,
    PublishedStatus,
} from "_graphql/__generated__/globalTypes";
import {
    extractVehicleNumberFromDetails,
    useUrlState,
    withPermissions,
} from "utils";
import { useNavigate, useSearch } from "react-location";

import { Action } from "components/buttons/action-button";
import AttachSupportingDocumentsContainer from "pages/create-slip/attach-supporting-documents";
import { CheckBadgeIcon } from "@heroicons/react/24/outline";
import { LocationGenerics } from "router/location";
import { Offers_rows } from "_graphql/queries/__generated__/Offers";
import RemoveContainer from "./remove";
import ReopenContainer from "./reopen";
import SendEmailsContainer from "./send-documents";
import SendMessageContainer from "components/containers/place-it-message-thread";
import ViewNotesContainer from "./view";
import YearPicker from "layouts/calendar-wrapper/year-picker";
import clsx from "clsx";
import numeral from "numeral";
import { useFormik } from "formik";
import { useOffers } from "hooks/data/use-offers";
import { usePagination } from "hooks";
import { useUpdateBubble } from "hooks/data/use-update-bubble";

type Props = {};

const CreateClosingPage: FC<Props> = () => {
    const [modal, setModal] = useUrlState("modal");
    const searchParams = useSearch<LocationGenerics>();
    const navigate = useNavigate<LocationGenerics>();
    const { offset, limit, setPage, search } = usePagination();
    const { updateBubble } = useUpdateBubble();

    const form = useFormik<any>({
        initialValues: {
            insurer: "",
            payment_status: "",
            business: "",
        },
        onSubmit: (values) => {},
    });

    const filter = useMemo(
        () => ({
            page: searchParams.page || 1,
            pageSize: searchParams.pageSize || 10,
            search: (search || "").toString(),
            args: {
                status: ["CLOSED"],
                insurer_id: form.values.insurer || "",
                year: searchParams.year?.toString() || "",
                offer_from: searchParams.offer_from || "",
                class_of_business_id: form.values.business || "",
                payment_status: form.values.payment_status
                    ? [form.values.payment_status]
                    : [],
                claim_status: [],
            },
        }),
        [
            form.values.insurer,
            form.values.business,
            form.values.payment_status,
            searchParams.page,
            searchParams.pageSize,
            searchParams.year,
            search,
            searchParams.offer_from,
        ],
    );

    const dispatchAction =
        (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
        () => {
            navigate({
                search: (old) => ({
                    ...old,
                    id,
                    modal: action,
                }),
            });
        };

    const expandItem = (id: string) => {
        navigate({
            to: `./${id}/credit-notes`,
        });
    };

    const updateThreadBubble = (ids: string[]) => {
        updateBubble({
            variables: {
                ids,
            },
        })
            .then(() => {})
            .catch((err) => {});
    };

    const { offers, loading, refetch, count } = useOffers(filter);

    return (
        <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
            <OfficeHeader
                renderActions={() => (
                    <>
                        <YearPicker />
                    </>
                )}
            />
            {/* {JSON.stringify(form.values.insurer, null, 2)} */}
            <div className="flex flex-1 overflow-y-auto">
                <div className="flex-1 mx-auto min-w-0  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
                    <TableComponent
                        title={"offers"}
                        hasSearch
                        loading={loading}
                        columns={[
                            {
                                name: "Policy #",
                                accessor: "offer_detail.policy_number",
                                render: (value: Offers_rows) => (
                                    <div className="flex flex-col items-start flex-wrap break-all break-words">
                                        <div className="flex flex-row items-center gap-x-1">
                                            {value?.offer_published &&
                                                value?.offer_published
                                                    ?.published_status ===
                                                    PublishedStatus.PUBLISHED && (
                                                    <CheckBadgeIcon
                                                        className={clsx(
                                                            "w-4 h-4 mr-1",
                                                            "text-green-600",
                                                        )}
                                                    />
                                                )}
                                            <span className="font-bold text-xs overflow-hidden text-ellipsis  ">
                                                {value.offer_detail
                                                    ?.policy_number || "N/A"}
                                            </span>
                                        </div>
                                        <div
                                            className={clsx(
                                                "p-0.5 rounded-full",
                                                {
                                                    " text-green-600":
                                                        value.offer_from ===
                                                        "INTERNAL",
                                                    " text-yellow-600":
                                                        value.offer_from !==
                                                        "INTERNAL",
                                                },
                                                "text-xs font-medium",
                                            )}>
                                            <span>{value.offer_from}</span>
                                        </div>
                                    </div>
                                ),
                            },
                            {
                                name: "Insurance Company",
                                accessor: "insurer.insurer_company_name",
                                render: (value: Offers_rows) => (
                                    <div className="flex flex-col text-xs">
                                        <span className="font-medium">
                                            {value.insurer
                                                ?.insurer_company_name || "N/A"}
                                        </span>
                                        <span className="">
                                            {value.insurer
                                                ?.insurer_company_email ||
                                                "N/A"}
                                        </span>
                                    </div>
                                ),
                            },
                            {
                                name: "Insured",
                                accessor: "offer_detail.insured_by",
                                render: (value: Offers_rows) => {
                                    const details = JSON.parse(
                                        value?.offer_detail?.offer_details ||
                                            "[]",
                                    );
                                    return (
                                        <div className="flex flex-col text-xs ">
                                            <span className="font-medium">
                                                {value.offer_detail
                                                    ?.insured_by || "N/A"}
                                            </span>
                                            <span className="">
                                                {[
                                                    "Motor Comprehensive Fleet",
                                                    "Motor Comprehensive",
                                                    "Motor Comprehensive (Automobile Fac Facility)",
                                                ].includes(
                                                    value?.classofbusiness
                                                        ?.business_name || "",
                                                )
                                                    ? extractVehicleNumberFromDetails(
                                                          details,
                                                      )
                                                    : ""}
                                            </span>
                                        </div>
                                    );
                                },
                            },
                            {
                                name: "Class Of Business",
                                accessor: "classofbusiness.business_name",
                                render: (value: Offers_rows) => (
                                    <div className="flex flex-col text-xs">
                                        <span className="font-medium">
                                            {value.classofbusiness
                                                ?.business_name || "N/A"}
                                        </span>
                                    </div>
                                ),
                            },
                            {
                                name: "Sum Insured",
                                accessor: "sum_insured",
                                render: (value: Offers_rows) => (
                                    <div className="flex flex-col text-xs">
                                        <span className="font-medium">
                                            {value?.offer_detail?.currency}{" "}
                                            {numeral(value.sum_insured).format(
                                                "0,0 ",
                                            ) || "N/A"}
                                        </span>
                                    </div>
                                ),
                            },
                            {
                                name: "Premium",
                                accessor: "premium",
                                render: (value: Offers_rows) => (
                                    <div className="flex flex-col text-xs">
                                        <span className="font-medium">
                                            {value?.offer_detail?.currency}{" "}
                                            {numeral(value.premium).format(
                                                "0,0 ",
                                            ) || "N/A"}
                                        </span>
                                    </div>
                                ),
                            },
                            {
                                name: "Offer Date",
                                accessor: "created_at",
                                render: (value: Offers_rows) =>
                                    new Date(
                                        value.created_at,
                                    ).toLocaleDateString(),
                            },
                            {
                                name: "Expiry Status",
                                accessor: "expiry_status",
                                render: (value: Offers_rows) => (
                                    <div className="flex flex-col items-start">
                                        <div
                                            className={clsx(
                                                "p-1 rounded-lg",
                                                {
                                                    "bg-green-600/30 text-green-600":
                                                        value.expiry_status ===
                                                        "ACTIVE",
                                                    "bg-red-50/30 text-red-600":
                                                        value.expiry_status ===
                                                        "EXPIRED",
                                                },
                                                "text-xs font-medium",
                                            )}>
                                            <span>{value.expiry_status}</span>
                                        </div>
                                    </div>
                                ),
                            },
                            {
                                name: "Payment Status",
                                accessor: "payment_status",
                                render: (value: Offers_rows) => (
                                    <div className="flex">
                                        <div
                                            className={clsx(
                                                "p-1 rounded-lg",
                                                {
                                                    "bg-green-600/30 text-green-600":
                                                        value.payment_status ===
                                                        "PAID",
                                                    "bg-red-300/30 text-red-600":
                                                        value.payment_status ===
                                                        "UNPAID",
                                                    "bg-sky-600/30 text-sky-600":
                                                        value.payment_status ===
                                                        "PARTPAYMENT",
                                                },
                                                "text-xs font-medium",
                                            )}>
                                            <span>{value.payment_status}</span>
                                        </div>
                                    </div>
                                ),
                            },
                            {
                                name: "Created By",
                                accessor: "employee.employee_first_name",
                                render: (value: Offers_rows) => (
                                    <div className="flex flex-row leading-0">
                                        <span>
                                            {
                                                value?.employee
                                                    ?.employee_first_name
                                            }{" "}
                                            {
                                                value?.employee
                                                    ?.employee_last_name
                                            }
                                        </span>
                                    </div>
                                ),
                            },

                            {
                                name: "Actions",
                                accessor: "actions",
                                render: (value: Offers_rows) => (
                                    <>
                                        <ActionButton
                                            action="view"
                                            tooltip="View Documents & History"
                                            onClick={dispatchAction(
                                                value.offer_id +
                                                    ":" +
                                                    value?.offer_from +
                                                    ":" +
                                                    value?.place_it_id,
                                                "view",
                                            )}
                                        />
                                        {value.offer_from !== "INTERNAL" && (
                                            <ActionButton
                                                action="chat"
                                                tooltip="View Message Thread"
                                                onClick={dispatchAction(
                                                    value?.place_it_id || "",
                                                    "chat",
                                                )}
                                                count={
                                                    value.thread_notification?.filter(
                                                        (item) =>
                                                            item?.status !==
                                                            "READ",
                                                    ).length
                                                }
                                                callBack={() =>
                                                    updateThreadBubble(
                                                        value.thread_notification?.map(
                                                            (item) =>
                                                                item?.id || "",
                                                        ) || [],
                                                    )
                                                }
                                            />
                                        )}
                                        {withPermissions([
                                            "*:*",
                                            "offers:manage",
                                            "offers:*",
                                            "offers:send",
                                        ])(
                                            <ActionButton
                                                action="send"
                                                tooltip="Send Notes"
                                                onClick={dispatchAction(
                                                    value?.offer_id,
                                                    "send",
                                                )}
                                            />,
                                        )}
                                        <ActionButton
                                            action="goto"
                                            tooltip="View Credit Notes"
                                            onClick={() =>
                                                expandItem(value?.offer_id)
                                            }
                                        />
                                        {withPermissions([
                                            "*:*",
                                            "offers:reinstate",
                                        ])(
                                            <ActionButton
                                                action="reject"
                                                tooltip="Reopen Offer"
                                                onClick={dispatchAction(
                                                    value?.offer_id,
                                                    "reject",
                                                )}
                                            />,
                                        )}
                                        <Show
                                            if={
                                                value.offer_from === "INTERNAL"
                                            }>
                                            {withPermissions([
                                                "*:*",
                                                "offers:*",
                                                "offers:manage",
                                                "offers:update",
                                            ])(
                                                <ActionButton
                                                    action="attach"
                                                    tooltip="Add Supporting Documents"
                                                    onClick={dispatchAction(
                                                        value?.offer_id,
                                                        "import",
                                                    )}
                                                />,
                                            )}
                                        </Show>
                                        {withPermissions([
                                            "*:*",
                                            "offers:*",
                                            "offers:manage",
                                            "offers:delete",
                                        ])(
                                            <ActionButton
                                                action="remove"
                                                tooltip="Delete Offer"
                                                onClick={dispatchAction(
                                                    value?.offer_id,
                                                    "remove",
                                                )}
                                            />,
                                        )}
                                    </>
                                ),
                            },
                        ]}
                        data={offers}
                        refetch={refetch}
                        renderHeaderItems={() => (
                            <div className="flex items-center space-x-2">
                                <BusinessPicker
                                    id="business"
                                    label="Business"
                                    rawId
                                    labelHidden
                                    {...form}
                                />
                                <RegularSelectInput
                                    id="offer_from"
                                    hideLabel
                                    label="Offer From"
                                    placeholder="Filter by Offer From"
                                    options={["INTERNAL", "PLACEIT"].map(
                                        (tab) => ({
                                            value: tab,
                                            label: tab,
                                        }),
                                    )}
                                    values={{
                                        offer_from:
                                            searchParams?.offer_from || "",
                                    }}
                                    handleChange={(evt: any) => {
                                        navigate({
                                            search: (old) => ({
                                                ...old,
                                                offer_from: evt.target.value,
                                                page: 1,
                                            }),
                                        });
                                    }}
                                />
                                <RegularInsurerPicker
                                    placeholder="Filter by Cedant"
                                    id={"insurer"}
                                    rawId
                                    labelHidden
                                    label={""}
                                    {...form}
                                />
                                <RegularSelectInput
                                    id="payment_status"
                                    label="Payment Status"
                                    hideLabel
                                    options={[
                                        {
                                            label: "Paid",
                                            value: Payment_status.PAID,
                                        },
                                        {
                                            label: "Partpayment",
                                            value: Payment_status.PARTPAYMENT,
                                        },
                                        {
                                            label: "Unpaid",
                                            value: Payment_status.UNPAID,
                                        },
                                    ]}
                                    {...form}
                                />
                            </div>
                        )}
                        pagination={() => (
                            <Paginator
                                offset={offset}
                                limit={limit}
                                totalItems={count || 0}
                                currentSize={offers.length}
                                setOffset={setPage}
                            />
                        )}
                    />
                </div>
            </div>
            {searchParams?.id && modal === "view" && (
                <ViewNotesContainer
                    open={modal === "view"}
                    setOpen={(val: boolean) =>
                        setModal(val ? "view" : undefined)
                    }
                />
            )}
            {searchParams?.id && modal === "remove" && (
                <RemoveContainer
                    open={modal === "remove"}
                    setOpen={(val: boolean) =>
                        setModal(val ? "remove" : undefined)
                    }
                />
            )}
            {searchParams?.id && modal === "reject" && (
                <ReopenContainer
                    open={modal === "reject"}
                    setOpen={(val: boolean) =>
                        setModal(val ? "reject" : undefined)
                    }
                />
            )}
            {searchParams?.id && modal === "send" && (
                <SendEmailsContainer
                    open={modal === "send"}
                    setOpen={(val: boolean) =>
                        setModal(val ? "send" : undefined)
                    }
                />
            )}
            {searchParams?.id && modal === "chat" && (
                <SendMessageContainer
                    open={modal === "chat"}
                    setOpen={(val: boolean) =>
                        setModal(val ? "chat" : undefined)
                    }
                />
            )}
            {searchParams?.id && modal === "import" && (
                <AttachSupportingDocumentsContainer
                    open={modal === "import"}
                    setOpen={(val: boolean) =>
                        setModal(val ? "import" : undefined)
                    }
                />
            )}
        </main>
    );
};

export default CreateClosingPage;
